/* Form Component to Add or Edit Customer details*/

import { useContext, useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select } from "antd";
import { FormRow } from "../../components/form/form-row";
import { TContext, TTimezone } from "../../types/config";
import { eResultCode } from "../../utils/enum";
import useFetch from "../../hooks/useFetch";
import useToast from "../../hooks/useToast";
import { ToastOpen, ToastType } from "../../state/toast/slice";
import {
  GetTimeZones,
  UpdateTimeZonePreference,
} from "../../utils/api.constant";
import { UserContext } from "../../context";
import "../../index.css";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { dateOnlyFormat } from "../../utils/constants";

const { Option } = Select;

type DisplayMode = "default" | "large" | "xl";

export default function ProfileControlForm() {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const context: TContext = useContext(UserContext);
  const [regions, setRegions] = useState<string[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [timezones, setTimezones] = useState<TTimezone[]>([]);
  const [timezonesData, setTimezonesData] = useState<any>([]);
  const { post } = useFetch();
  const { onShowToast } = useToast();

  const [displayMode, setDisplayMode] = useState<DisplayMode>(
    () => (localStorage.getItem("displayMode") as DisplayMode) || "default"
  );
  // const [isFullscreen, setIsFullscreen] = useState<boolean>(
  //   () => localStorage.getItem("isFullscreen") === "true"
  // );
  const [autoClear, setAutoClear] = useState<boolean>(
    () => localStorage.getItem("autoClear") === "true"
  );
  const [autoClearDuration, setAutoClearDuration] = useState<number>(() =>
    parseInt(localStorage.getItem("autoClearDuration") || "10", 10)
  );
  const zoomMarks = {
    0: { style: { fontSize: 12 }, label: "Default" },
    1: { style: { fontSize: 12 }, label: "Large" },
    2: { style: { fontSize: 12 }, label: "XL" },
  };
  const displayModes = ["default", "large", "xl"];

  useEffect(() => {
    const prevRegion = context?.user?.region;
    const prevTimeZonePref = context?.user?.timeZonePreference;

    if (prevRegion && prevTimeZonePref && timezonesData[prevRegion]) {
      form.setFieldsValue({
        region: context?.user?.region,
        timeZonePreference: context?.user?.timeZonePreference,
        dateFormat: context?.user?.dateFormat || dateOnlyFormat,
        currency: context?.user?.currency || "USD",
        numberFormat: context?.user?.numberFormat || "#,###,###.##",
      });
      setSelectedRegion(prevRegion);
      setTimezones(timezonesData[prevRegion] || []);
    }
  }, [context?.user, timezonesData]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    // Reset the timezone selection when the region changes
    form.setFieldsValue({ timeZonePreference: undefined });
    // Update the list of timezones based on the selected region
    setTimezones(timezonesData[region] || []);
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetTimeZones, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setTimezonesData(data);
        const regionKeys = Object.keys(data);
        setRegions(regionKeys);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle on submit event
  const onFinish = async (values: any) => {
    try {
      const payload = {
        data: {
          ...values,
        },
      };
      setIsLoading(true);

      const response = await post(UpdateTimeZonePreference, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        context?.onUpdate?.({
          user: { ...context?.user, ...values },
        });

        // Save additional settings to localStorage only when Update is clicked
        localStorage.setItem("autoClear", String(autoClear));
        localStorage.setItem("autoClearDuration", String(autoClearDuration));
        localStorage.setItem("displayMode", displayMode);
        // localStorage.setItem("isFullscreen", String(isFullscreen));

        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-control-form">
      <h2>Preferences</h2>
      <div style={{ marginTop: "1rem" }}>
        <Form
          form={form}
          name="register"
          onFinish={onFinish}
          layout="vertical"
          autoComplete="off"
          size="middle"
        >
          <Row gutter={16}>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="region"
                label="Select Region"
                rules={[{ required: true, message: "Please select a region!" }]}
              >
                <Select
                  size="large"
                  disabled={isLoading}
                  placeholder="Select a region"
                  onChange={handleRegionChange}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {regions.map((region) => (
                    <Option key={region} value={region}>
                      {region}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="timeZonePreference"
                label="Select Timezone"
                rules={[
                  { required: true, message: "Please select a timezone!" },
                ]}
              >
                <Select
                  size="large"
                  showSearch
                  placeholder="Select a timezone"
                  disabled={!selectedRegion || isLoading}
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {timezones.map((tz) => (
                    <Option key={tz.ianaId} value={tz.ianaId}>
                      {tz.displayName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="currency"
                label="Currency"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please select a currency",
                //   },
                // ]}
              >
                <Select
                  size="large"
                  disabled={true || isLoading}
                  placeholder="Select your currency"
                  options={[
                    { value: "USD", label: "USD - United States Dollar" },
                    { value: "EUR", label: "EUR - Euro" },
                    { value: "INR", label: "INR - Indian Rupee" },
                    { value: "GBP", label: "GBP - British Pound Sterling" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="numberFormat"
                label="Number Format"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter the number format",
                //   },
                // ]}
              >
                <Input
                  size="small"
                  disabled={isLoading || true}
                  placeholder="##,##,###.##"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item
                name="dateFormat"
                label="Date Format"
                rules={[
                  {
                    required: true,
                    message: "Please select a date format",
                  },
                ]}
              >
                <Select
                  size="large"
                  disabled={isLoading}
                  placeholder="Select a date format"
                  options={[
                    { value: "MM/dd/yyyy", label: "MM/DD/YYYY" },
                    { value: "dd/MM/yyyy", label: "DD/MM/YYYY" },
                    { value: "dd-MMM-yyyy", label: "DD-MMM-YYYY" },
                  ]}
                />
              </Form.Item>
            </Col>

            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item label="Zoom Controller">
                <Select
                  value={displayMode}
                  onChange={(val) => setDisplayMode(val as DisplayMode)}
                >
                  <Option value="default">Zoom: Default</Option>
                  <Option value="large">Zoom: Large</Option>
                  <Option value="xl">Zoom: XL</Option>
                </Select>
              </Form.Item>
            </Col> */}
            {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
              <Form.Item label="Auto Clear">
                <Select
                  value={autoClear ? "on" : "off"}
                  onChange={(value) => setAutoClear(value === "on")}
                >
                  <Option value="on">On</Option>
                  <Option value="off">Off</Option>
                </Select>
              </Form.Item>
            </Col>
            {autoClear && (
              <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                <Form.Item label="Clear After">
                  <Select
                    value={autoClearDuration}
                    onChange={setAutoClearDuration}
                  >
                    <Option value={5}>5 seconds</Option>
                    <Option value={10}>10 seconds</Option>
                    <Option value={30}>30 seconds</Option>
                    <Option value={60}>60 seconds</Option>
                  </Select>
                </Form.Item>
              </Col> */}
            {/* )} */}

            {/* <Tooltip title={isFullscreen ? "Exit focus mode" : "Focus mode"}>
          <Button
            icon={
              isFullscreen ? (
                <FullscreenExitOutlined />
              ) : (
                <FullscreenOutlined />
              )
            }
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
        </Tooltip> */}
          </Row>

          <FormRow className="formRow">
            <Button
              type="primary"
              onClick={form.submit}
              disabled={isLoading}
              loading={isLoading}
              className="btnDimensions"
            >
              Update
            </Button>
          </FormRow>
        </Form>
      </div>
    </div>
  );
}
